/**
 * features/parent/SubjectChoices.tsx — Parent portal → Subjects (the school,
 * 1 Nov 2026). A Form 3 family chooses their child's IGCSE subjects for
 * Form 4; a Form 5 family chooses AS subjects. Ported from Al-Nur's parent
 * form (alnur-portal ParentExtras SubjectChoices) with LKC's rules, which live
 * in lib/subjectChoices (tested): English and Mathematics compulsory at IGCSE
 * and six more, Add Maths among them; three to five at AS.
 *
 * Choosing next year's subjects is not an academic result, so the fees hold
 * does not hide it (as with Books, Exams and Timetable). The choices go to
 * `reportSubjectChoices/{year}__{studentId}`; the rules let only this child's
 * parent write them, and only while the window is open.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { AlertTriangle, Check, CheckCircle2, Info, X } from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { SoftCard, btnGhost, btnPrimary } from '@/features/finance/ui';
import {
  CHOICES, INFO, LEVEL_LABEL, cambridgeCode, checkChoices, choiceId, infoFor, infoId, isOpen,
  type Level, type SubjectChoiceDoc, type SubjectChoiceSettings, type SubjectInfoEdit,
} from '@/lib/subjectChoices';
import type { Student } from '@/types';

const muted = 'text-xs text-gray-500 dark:text-gray-400';
const heading = 'px-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400';

export default function SubjectChoices({ child, className, level, settings }: {
  child: Student; className: string; level: Level; settings: SubjectChoiceSettings;
}) {
  const { userData } = useAuth();
  const { subjects } = useData();
  const L = settings[level];
  const first = child.fullName.split(' ')[0];
  const open = isOpen(settings, Date.now());
  const id = choiceId(settings.year, child.id);
  const [edits, setEdits] = useState<Record<string, SubjectInfoEdit>>({});
  const [picked, setPicked] = useState<string[]>([]);
  const [saved, setSaved] = useState<SubjectChoiceDoc | null>(null);
  const [about, setAbout] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    getDocs(collection(db, INFO))
      .then((s) => setEdits(Object.fromEntries(s.docs.map((d) => [d.id, d.data() as SubjectInfoEdit]))))
      .catch(() => setEdits({}));
    setSaved(null);
    setPicked([]);
    getDoc(doc(db, CHOICES, id)).then((s) => {
      if (!s.exists()) return;
      const d = s.data() as SubjectChoiceDoc;
      setSaved(d);
      setPicked(d.subjects ?? []);
    }).catch(() => { /* none yet */ });
  }, [id]);

  const nameOf = useMemo(() => {
    const m = new Map(subjects.map((s) => [s.id, s.name]));
    return (x: string) => m.get(x) ?? x;
  }, [subjects]);
  const code = (x: string) => cambridgeCode(level, nameOf(x));
  const check = checkChoices(settings, level, picked, nameOf);
  const offered = [...L.offered].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  const howMany = L.min === L.max ? `${L.min}` : `${L.min} to ${L.max}`;
  const closes = settings.closesOn
    ? new Date(`${settings.closesOn}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
    : '';

  const toggle = (x: string) => {
    if (!open) return;
    setMessage(null);
    setPicked((cur) => (cur.includes(x) ? cur.filter((c) => c !== x) : cur.length < L.max ? [...cur, x] : cur));
  };

  const send = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const d: SubjectChoiceDoc = {
        year: settings.year, level, studentId: child.id, studentName: child.fullName, className,
        subjects: picked, submittedBy: userData?.uid ?? '', submittedByName: userData?.displayName ?? '',
        updatedAt: new Date().toISOString(),
      };
      await setDoc(doc(db, CHOICES, id), d);
      setSaved(d);
      setMessage({ ok: true, text: `Sent to the academic office.${closes ? ` You can change them until ${closes}.` : ''}` });
    } catch {
      setMessage({ ok: false, text: 'Your choices could not be sent. The window may have closed; check your connection and try again.' });
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ x, fixed = false }: { x: string; fixed?: boolean }) => {
    const on = fixed || picked.includes(x);
    return (
      <div className="flex gap-2">
        <button
          type="button" disabled={fixed || !open} onClick={() => toggle(x)} aria-pressed={on}
          className={`flex-1 min-w-0 min-h-[52px] rounded-xl border px-3 py-2 flex items-center gap-3 text-left ${on
            ? 'border-primary bg-primary/5 dark:border-blue-400 dark:bg-blue-950/40'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`}
        >
          <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 border-2 ${on
            ? (fixed ? 'bg-gray-500 border-gray-500 dark:bg-gray-400 dark:border-gray-400' : 'bg-primary border-primary dark:bg-blue-400 dark:border-blue-400')
            : 'border-gray-300 dark:border-gray-500'}`}>
            {on && <Check size={15} className="text-white dark:text-gray-900" aria-hidden="true" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-gray-900 dark:text-gray-50">{nameOf(x)}</span>
            {code(x) && <span className="block text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">Cambridge {LEVEL_LABEL[level]} {code(x)}</span>}
          </span>
        </button>
        <button type="button" className={btnGhost} onClick={() => setAbout(x)} aria-label={`About ${nameOf(x)}`}>About</button>
      </div>
    );
  };

  const a = about ? infoFor(nameOf(about), edits[infoId(level, about)]) : null;

  return (
    <div className="flex flex-col gap-4">
      <SoftCard className="flex flex-col gap-2">
        <p className="text-sm text-gray-700 dark:text-gray-200">
          {level === 'igcse'
            ? <>{first} starts IGCSE in {L.forLabel} in {settings.year}: {L.compulsory.map(nameOf).join(' and ')}, and <b>{howMany} subjects</b> you choose. Additional Mathematics counts as one of them.</>
            : <>Choose the <b>{howMany} AS subjects</b> {first} will take in {settings.year}.</>}
          {' '}Tap <b>About</b> to read what a subject covers, who it suits and where it can lead.
        </p>
        {open
          ? (closes && <p className={muted}>You can change your choices until {closes}.</p>)
          : <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Choices are closed{saved ? '. These are the choices you sent.' : '.'}</p>}
        {saved && <p className={muted}>Last sent {new Date(saved.updatedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} by {saved.submittedByName || 'you'}.</p>}
      </SoftCard>

      {L.compulsory.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Everyone takes">
          <h2 className={heading}>Everyone takes</h2>
          {L.compulsory.map((x) => <Row key={x} x={x} fixed />)}
        </section>
      )}

      <section className="flex flex-col gap-2" aria-label={`Choose ${howMany}`}>
        <h2 className={heading}>Choose {howMany} · {picked.length} chosen</h2>
        {offered.map((x) => <Row key={x} x={x} />)}
      </section>

      {(check.notes.length > 0 || check.blocking.some((b) => !b.startsWith('Choose '))) && (
        <div className="flex flex-col gap-2">
          {check.blocking.filter((b) => !b.startsWith('Choose ')).map((b) => (
            <div key={b} className="flex gap-2 rounded-r-xl border border-rose-200 border-l-4 border-l-rose-600 bg-rose-50 px-3 py-2.5 dark:bg-rose-950/40 dark:border-rose-800 dark:border-l-rose-400">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-rose-700 dark:text-rose-300" aria-hidden="true" />
              <span className="text-sm text-rose-900 dark:text-rose-100">{b}</span>
            </div>
          ))}
          {check.notes.map((n) => (
            <div key={n} className="flex gap-2 rounded-r-xl border border-amber-200 border-l-4 border-l-amber-500 bg-amber-50 px-3 py-2.5 dark:bg-amber-950/40 dark:border-amber-800 dark:border-l-amber-400">
              <Info size={16} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
              <span className="text-sm text-amber-900 dark:text-amber-100">{n}</span>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white/95 dark:bg-gray-900/95 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3">
          <span className="flex-1 text-sm font-semibold text-gray-900 dark:text-gray-50">{picked.length} chosen</span>
          <button type="button" className={btnPrimary} disabled={busy || check.blocking.length > 0} onClick={() => void send()}>
            {busy ? 'Sending...' : saved ? 'Send changes' : 'Send choices'}
          </button>
        </div>
      )}
      {message && (
        <div className={`flex gap-2 rounded-xl px-3 py-2.5 text-sm ${message.ok
          ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100'
          : 'bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100'}`} role="status">
          {message.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" aria-hidden="true" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />}
          <span>{message.text}</span>
        </div>
      )}

      {a && about && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/45 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="about-title" onClick={(e) => { if (e.target === e.currentTarget) setAbout(null); }}>
          <div className="w-full sm:max-w-lg max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-5 flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <h2 id="about-title" className="text-lg font-bold text-gray-900 dark:text-gray-50">{nameOf(about)}</h2>
                {code(about) && <p className={muted}>Cambridge {LEVEL_LABEL[level]} {code(about)}</p>}
              </div>
              <button type="button" onClick={() => setAbout(null)} aria-label="Close" className="w-9 h-9 rounded-lg inline-flex items-center justify-center text-gray-600 dark:text-gray-300"><X size={18} /></button>
            </div>
            {a.covers && <><h3 className={heading}>What it covers</h3><p className="text-sm text-gray-700 dark:text-gray-200">{a.covers}</p></>}
            {a.whoFor && <><h3 className={heading}>Who should choose it</h3><p className="text-sm text-gray-700 dark:text-gray-200">{a.whoFor}</p></>}
            {a.careers.length > 0 && (
              <>
                <h3 className={heading}>Where it can lead</h3>
                <div className="flex flex-wrap gap-1.5">
                  {a.careers.map((j) => <span key={j} className="rounded-full bg-primary/10 dark:bg-blue-950/50 px-2.5 py-1 text-xs font-semibold text-primary dark:text-blue-300">{j}</span>)}
                </div>
              </>
            )}
            {open && !L.compulsory.includes(about) && (
              <button type="button" className={btnPrimary} onClick={() => { toggle(about); setAbout(null); }}>
                {picked.includes(about) ? 'Remove from choices' : 'Add to choices'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
